import type { IRowNode } from 'ag-grid-community';
import type { MasterDetailRowMarkers, MasterRowWithId } from './types';

export const normalizeMasterRowId = (rowId: string) => rowId?.toUpperCase?.() ?? rowId;

export const buildMasterRows = <TData extends object>(
  sourceData: TData[],
  getRowId: (data: TData) => string
): MasterRowWithId<TData>[] =>
  sourceData.map((row) => ({ ...row, _masterRowId: getRowId(row) }));

/**
 * Interleave full-width detail rows under expanded masters.
 * Master row objects are pushed by reference so AG Grid can keep stable master identity.
 */
export const interleaveDetailRows = <TData extends object>(
  masterRows: MasterRowWithId<TData>[],
  isRowExpanded: (id: string) => boolean,
  shouldShowDetailRow?: (data: TData) => boolean
): object[] =>
  masterRows.flatMap((row) => {
    const showDetail =
      isRowExpanded(row._masterRowId) && shouldShowDetailRow?.(row as TData) !== false;
    if (!showDetail) {
      return [row];
    }
    return [row, { ...row, _isDetail: true, _parentId: row._masterRowId }];
  });

export const getMasterDetailRowId = (data: MasterDetailRowMarkers): string =>
  data._isDetail ? `detail-${data._parentId}` : data._masterRowId!;

/** After sort, keep each detail row immediately under its parent master. */
export const keepDetailRowsWithParent = ({ nodes }: { nodes: IRowNode[] }) => {
  const masters = nodes.filter(
    (node) => (node.data as MasterDetailRowMarkers | undefined)?._isDetail !== true
  );
  const detailsByParent = nodes.reduce<Map<string, IRowNode[]>>((map, node) => {
    const data = node.data as MasterDetailRowMarkers | undefined;
    if (data?._isDetail !== true || !data._parentId) {
      return map;
    }
    const siblings = map.get(data._parentId) ?? [];
    siblings.push(node);
    map.set(data._parentId, siblings);
    return map;
  }, new Map());

  const ordered = masters.flatMap((master) => {
    const parentId = (master.data as MasterDetailRowMarkers | undefined)?._masterRowId;
    const details = parentId ? detailsByParent.get(parentId) ?? [] : [];
    return [master, ...details];
  });

  const orderedSet = new Set(ordered);
  const leftovers = nodes.filter((node) => !orderedSet.has(node));
  nodes.splice(0, nodes.length, ...ordered, ...leftovers);
};
